import React, { useState, useEffect, useRef } from 'react';
import './IAEtAutomatisation.css';
import Header from '../Header/Header';
import Footer from '../footer/footer';
import { motion, AnimatePresence } from 'framer-motion';
import TopAnnouncementBar from '../TopAnnouncementBar/TopAnnouncementBar';
import WhatsAppButton from '../whatsapp/WhatsAppButton';
import { Link } from 'react-router-dom';
import { 
  FaRobot, 
  FaBrain, 
  FaSyncAlt, 
  FaChartLine,
  FaCogs,
  FaMagic,
  FaShieldAlt,
  FaRocket,
  FaUsers,
  FaLinkedin,
  FaCalendarAlt,
  FaSearch,
  FaArrowRight,
  FaCheckCircle,
  FaAward,
  FaClock,
  FaCode
} from 'react-icons/fa';
import { 
  SiGoogleanalytics,
  SiZapier
} from 'react-icons/si';
import { MdAutoAwesome, MdOutlineSpeed, MdOutlineSupportAgent, MdAnalytics, MdIntegrationInstructions } from 'react-icons/md';
import { GiAstronautHelmet, GiStarsStack, GiGalaxy, GiSpiralShell, GiCrystalBall, GiCyberEye, GiArtificialIntelligence, GiThunderSkull, GiBrain as GiBrainIcon, GiRocketThruster, GiCpu } from 'react-icons/gi';

const IAEtAutomatisation = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isCursorActive, setIsCursorActive] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef(null);
  let cursorTimeout;

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      setIsCursorActive(true);
      
      clearTimeout(cursorTimeout);
      cursorTimeout = setTimeout(() => setIsCursorActive(false), 100);
    };
    
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrollPercent = Math.min(1, Math.max(0, -rect.top / (rect.height - window.innerHeight)));
        setScrollProgress(scrollPercent);
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(cursorTimeout);
    };
  }, []);

  const solutionsData = [
    {
      title: "Chatbots & Assistants IA",
      tagline: "Support client 24/7",
      description: "Des assistants intelligents qui répondent automatiquement à vos clients, gèrent les demandes répétitives et libèrent vos équipes.",
      features: ["Réponses instantanées", "Multi-canaux", "Apprentissage continu", "Analytics avancés"],
      icon: <FaRobot />,
      color: "#8B5CF6",
      stat: "-85% de temps de réponse"
    },
    {
      title: "Analyse de données & Reporting IA",
      tagline: "Des insights en temps réel",
      description: "Transformez vos données brutes en décisions stratégiques avec des tableaux de bord intelligents et des prévisions prédictives.",
      features: ["Tableaux de bord personnalisés", "Alertes intelligentes", "Prévisions prédictives", "Visualisation avancée"],
      icon: <SiGoogleanalytics />,
      color: "#3B82F6",
      stat: "x3 la vitesse d'analyse"
    },
    {
      title: "Optimisation des processus IA",
      tagline: "Automatisation intelligente",
      description: "Identifiez et automatisez vos processus métier chronophages pour gagner en efficacité opérationnelle.",
      features: ["Audit des processus", "Workflows intelligents", "Intégration CRM", "Monitoring continu"],
      icon: <FaCogs />,
      color: "#10B981",
      stat: "75% de tâches automatisées"
    },
    {
      title: "Génération IA (Voix & Vidéo)",
      tagline: "Contenu illimité",
      description: "Créez automatiquement des vidéos, voix off et avatars pour votre marketing à grande échelle.",
      features: ["Vidéos avec avatars IA", "Voix off synthétiques", "Tutoriels animés", "Contenu multilingue"],
      icon: <FaMagic />,
      color: "#EC4899",
      stat: "10x plus de contenu"
    }
  ];

  const automationData = [
    {
      title: "Automatisation des tâches répétitives",
      tagline: "Gagnez des heures chaque jour",
      description: "Automatisez vos emails, relances, suivi client et facturation pour vous concentrer sur l'essentiel.",
      features: ["Workflows personnalisés", "Notifications automatiques", "Sauvegarde cloud", "Reporting intégré"],
      icon: <FaSyncAlt />,
      color: "#F59E0B",
      stat: "15h/semaine économisées"
    },
    {
      title: "Workflows intelligents",
      tagline: "Processus interconnectés",
      description: "Créez des scénarios automatisés complexes qui connectent vos équipes et vos outils.",
      features: ["Cartographie des processus", "Automatisation cross-team", "Reporting temps réel", "Optimisation continue"],
      icon: <FaMagic />,
      color: "#8B5CF6",
      stat: "+90% de coordination"
    },
    {
      title: "Intégration entre applications",
      tagline: "Tous vos outils connectés",
      description: "Connectez CRM, site web, Google Sheets, WhatsApp et réseaux sociaux pour une synchronisation parfaite.",
      features: ["API customisées", "Synchronisation temps réel", "Connecteurs multiples", "Maintenance proactive"],
      icon: <SiZapier />,
      color: "#06B6D4",
      stat: "100% de données synchronisées"
    },
    {
      title: "Automatisation marketing",
      tagline: "Des campagnes qui tournent seules",
      description: "Publiez automatiquement, nurturez vos leads et segmentez vos audiences sans effort.",
      features: ["Campagnes automatisées", "Segmentation avancée", "Analytics performance", "Optimisation A/B auto"],
      icon: <FaChartLine />,
      color: "#3B82F6",
      stat: "+40% de conversion"
    },
    {
      title: "Prospection LinkedIn automatisée",
      tagline: "Développez votre réseau",
      description: "Optimisez votre prospection B2B avec des messages personnalisés et un suivi intelligent.",
      features: ["Ajout d'abonnés ciblés", "Messages personnalisés", "Suivi automatique", "Reporting prospects"],
      icon: <FaLinkedin />,
      color: "#0077B5",
      stat: "3x plus de rendez-vous"
    },
    {
      title: "Automatisation interne",
      tagline: "Une équipe mieux coordonnée",
      description: "Gérez automatiquement les plannings, RH et coordination d'équipes pour une productivité maximale.",
      features: ["Gestion tâches auto", "Planning intelligent", "Suivi RH automatisé", "Coordination d'équipes"],
      icon: <FaCalendarAlt />,
      color: "#10B981",
      stat: "+50% de productivité"
    }
  ];

  const benefits = [
    { icon: <FaSyncAlt />, title: "Gain de temps", description: "Jusqu'à 80% de temps économisé", color: "#3B82F6" },
    { icon: <FaShieldAlt />, title: "Zéro erreur", description: "Processus standardisés", color: "#10B981" },
    { icon: <FaMagic />, title: "Processus fluides", description: "Exécution accélérée", color: "#8B5CF6" },
    { icon: <FaChartLine />, title: "Productivité", description: "+50% de performance", color: "#F59E0B" },
    { icon: <FaUsers />, title: "Image pro", description: "Innovation technologique", color: "#EC4899" },
    { icon: <FaRocket />, title: "Croissance", description: "CA multiplié", color: "#06B6D4" }
  ];

  const statsData = [
    { value: "+80%", label: "efficacité", icon: <FaRocket /> },
    { value: "-70%", label: "tâches manuelles", icon: <FaCogs /> },
    { value: "24/7", label: "opérationnel", icon: <FaRobot /> },
    { value: "+50%", label: "productivité", icon: <FaChartLine /> }
  ];

  const technologies = [
    { icon: <FaRobot />, name: "GPT-4", color: "#8B5CF6" },
    { icon: <SiGoogleanalytics />, name: "Analytics AI", color: "#3B82F6" },
    { icon: <SiZapier />, name: "Zapier", color: "#FF4A00" },
    { icon: <FaBrain />, name: "Machine Learning", color: "#10B981" },
    { icon: <GiCpu />, name: "Computer Vision", color: "#EC4899" },
    { icon: <MdIntegrationInstructions />, name: "API", color: "#F59E0B" }
  ];

  const getPreviewBadgeText = (isAI, index) => {
    if (isAI) {
      switch(index) {
        case 0: return "Support IA 24/7";
        case 1: return "Analytics prédictif";
        case 2: return "Processus intelligent";
        case 3: return "Génération automatique";
        default: return "Solution IA";
      }
    } else {
      switch(index) {
        case 0: return "Automatisation complète";
        case 1: return "Workflow intelligent";
        case 2: return "Connectivité totale";
        case 3: return "Marketing automatisé";
        case 4: return "Prospection augmentée";
        case 5: return "Gestion interne auto";
        default: return "Solution automatisation";
      }
    }
  };

  return (
    <div className="ia-container" ref={sectionRef}>
      <WhatsAppButton />
      <TopAnnouncementBar />
      <Header />
      
      {/* Cursor Glow */}
      <motion.div 
        className={`ia-cursor-glow ${isCursorActive ? 'active' : ''}`}
        animate={{
          x: mousePosition.x - 300,
          y: mousePosition.y - 300,
        }}
        transition={{ type: "spring", damping: 30, stiffness: 200, mass: 0.5 }}
      />

      {/* Particles */}
      <div className="ia-particles">
        {[...Array(60)].map((_, i) => (
          <div key={i} className="ia-particle" style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 20}s`,
            animationDuration: `${20 + Math.random() * 30}s`,
            width: `${1.5 + Math.random() * 3}px`,
            height: `${1.5 + Math.random() * 3}px`,
          }} />
        ))}
      </div>

      {/* Animated Orbs */}
      <div className="ia-orbs">
        <div className="ia-orb ia-orb-1" style={{ transform: `translate(${scrollProgress * 100}px, ${scrollProgress * 50}px)` }} />
        <div className="ia-orb ia-orb-2" style={{ transform: `translate(-${scrollProgress * 80}px, -${scrollProgress * 60}px)` }} />
        <div className="ia-orb ia-orb-3" style={{ transform: `translate(${scrollProgress * 60}px, -${scrollProgress * 40}px)` }} />
        <div className="ia-orb ia-orb-4" style={{ transform: `translate(-${scrollProgress * 70}px, ${scrollProgress * 70}px)` }} />
      </div>

      {/* Floating Elements */}
      <div className="ia-floating">
        <GiGalaxy className="ia-float ia-float-1" />
        <GiSpiralShell className="ia-float ia-float-2" />
        <GiAstronautHelmet className="ia-float ia-float-3" />
        <GiStarsStack className="ia-float ia-float-4" />
        <GiCrystalBall className="ia-float ia-float-5" />
        <GiCyberEye className="ia-float ia-float-6" />
        <GiArtificialIntelligence className="ia-float ia-float-7" />
        <GiThunderSkull className="ia-float ia-float-8" />
        <GiBrainIcon className="ia-float ia-float-9" />
        <GiRocketThruster className="ia-float ia-float-10" />
        <GiCpu className="ia-float ia-float-11" />
      </div>
      
      {/* ========== HERO BANNER ========== */}
      <section className="ia-hero">
        <div className="ia-hero-overlay"></div>
        <div className="ia-hero-inner">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="ia-hero-badge"
          >
            <GiArtificialIntelligence />
            <span>IA & Automation Excellence</span>
            <GiStarsStack />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="ia-hero-title"
          >
            L'intelligence artificielle au service<br />
            <span className="ia-gradient-text">de votre productivité</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="ia-hero-subtitle"
          >
            Automatisez vos tâches, analysez vos données et transformez votre entreprise 
            avec nos solutions d'IA et d'automatisation sur mesure.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="ia-hero-buttons"
          >
            <Link to="/reservation-appel">
              <button className="ia-btn ia-btn-primary">
                <MdAutoAwesome /> Optimiser mon entreprise <FaArrowRight />
              </button>
            </Link>
            <Link to="/realisations">
              <button className="ia-btn ia-btn-secondary">
                <FaCode /> Voir nos cas clients
              </button>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="ia-hero-stats"
          >
            {statsData.map((stat, idx) => (
              <div key={idx} className="ia-stat-card">
                <div className="ia-stat-icon">{stat.icon}</div>
                <div className="ia-stat-value">{stat.value}</div>
                <div className="ia-stat-label">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ========== BENEFITS SECTION ========== */}
      <section className="ia-benefits">
        <div className="ia-container-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="ia-section-header"
          >
            <div className="ia-badge">Pourquoi l'IA</div>
            <h2>Les bénéfices <span className="ia-gradient-text">de l'automatisation</span></h2>
            <p>Découvrez comment l'IA transforme votre façon de travailler</p>
          </motion.div>

          <div className="ia-benefits-grid">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ y: -8 }}
                className="ia-benefit-card"
                style={{ '--ia-card-color': benefit.color }}
              >
                <div className="ia-benefit-icon" style={{ color: benefit.color }}>{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
                <div className="ia-benefit-glow" style={{ background: `radial-gradient(circle, ${benefit.color}20, transparent)` }} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== SOLUTIONS IA SECTION - TABS ========== */}
      <section className="ia-solutions">
        <div className="ia-container-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="ia-section-header"
          >
            <div className="ia-badge">Solutions IA</div>
            <h2>Services d'<span className="ia-gradient-text">intelligence artificielle</span></h2>
            <p>Des solutions IA sur mesure pour transformer vos processus métier</p>
          </motion.div>

          {/* Tabs navigation */}
          <div className="ia-tabs-nav">
            {solutionsData.map((sol, idx) => (
              <button
                key={idx}
                className={`ia-tab-btn ${activeTab === idx ? 'active' : ''}`}
                onClick={() => setActiveTab(idx)}
                style={{ '--ia-tab-color': sol.color }}
              >
                <span className="ia-tab-icon">{sol.icon}</span>
                <span className="ia-tab-title">{sol.title}</span>
              </button>
            ))}
          </div>

          {/* Tab content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="ia-tab-content"
            >
              <div className="ia-tab-left">
                <div className="ia-solution-tagline" style={{ color: solutionsData[activeTab].color }}>
                  {solutionsData[activeTab].tagline}
                </div>
                <h3>{solutionsData[activeTab].title}</h3>
                <p>{solutionsData[activeTab].description}</p>
                <div className="ia-features-list">
                  {solutionsData[activeTab].features.map((feat, i) => (
                    <div key={i} className="ia-feature-item">
                      <FaCheckCircle style={{ color: solutionsData[activeTab].color }} />
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
                <div className="ia-solution-stat" style={{ background: `${solutionsData[activeTab].color}15`, borderLeftColor: solutionsData[activeTab].color }}>
                  <FaRocket />
                  <span><strong>{solutionsData[activeTab].stat}</strong></span>
                </div>
              </div>
              <div className="ia-tab-right">
                <div className="ia-preview-card" style={{ background: `linear-gradient(135deg, ${solutionsData[activeTab].color}10, rgba(10,17,40,0.4))` }}>
                  <div className="ia-preview-icon" style={{ color: solutionsData[activeTab].color }}>
                    {solutionsData[activeTab].icon}
                  </div>
                  <div className="ia-preview-badge">
                    {getPreviewBadgeText(true, activeTab)}
                  </div>
                  <div className="ia-preview-stats">
                    <span>🤖 IA native</span>
                    <span>⚡ Auto-apprentissage</span>
                    <span>📊 Analytics</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ========== AUTOMATION SECTION - TABS ========== */}
      <section className="ia-automation">
        <div className="ia-container-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="ia-section-header"
          >
            <div className="ia-badge">Solutions Automation</div>
            <h2>Services d'<span className="ia-gradient-text">automatisation</span></h2>
            <p>Automatisez vos processus pour gagner en efficacité</p>
          </motion.div>

          <div className="ia-automation-grid">
            {automationData.map((auto, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ y: -8 }}
                className="ia-automation-card"
                style={{ '--ia-auto-color': auto.color }}
              >
                <div className="ia-automation-icon" style={{ color: auto.color }}>{auto.icon}</div>
                <h3>{auto.title}</h3>
                <p>{auto.description}</p>
                <div className="ia-automation-features">
                  {auto.features.slice(0, 2).map((feat, i) => (
                    <span key={i} className="ia-auto-feature">{feat}</span>
                  ))}
                </div>
                <div className="ia-automation-stat" style={{ background: `${auto.color}15` }}>
                  <FaRocket />
                  <span><strong>{auto.stat}</strong></span>
                </div>
                <div className="ia-automation-glow" style={{ background: `radial-gradient(circle, ${auto.color}20, transparent)` }} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== PROCESS SECTION ========== */}
      <section className="ia-process">
        <div className="ia-container-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="ia-section-header"
          >
            <div className="ia-badge">Notre méthode</div>
            <h2>Approche <span className="ia-gradient-text">d'implémentation</span></h2>
            <p>Une méthodologie éprouvée pour une intégration réussie</p>
          </motion.div>

          <div className="ia-process-steps">
            {[
              { step: "01", title: "Audit", desc: "Analyse de vos processus", icon: <FaSearch />, color: "#3B82F6" },
              { step: "02", title: "Stratégie", desc: "Plan d'action personnalisé", icon: <FaBrain />, color: "#8B5CF6" },
              { step: "03", title: "Développement", desc: "Création des solutions", icon: <FaCogs />, color: "#10B981" },
              { step: "04", title: "Intégration", desc: "Connexion à vos outils", icon: <SiZapier />, color: "#F59E0B" },
              { step: "05", title: "Optimisation", desc: "Tests & ajustements", icon: <FaChartLine />, color: "#EC4899" },
              { step: "06", title: "Support", desc: "Maintenance continue", icon: <FaSyncAlt />, color: "#06B6D4" }
            ].map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ y: -5 }}
                className="ia-process-step"
              >
                <div className="ia-step-number" style={{ backgroundColor: step.color }}>{step.step}</div>
                <div className="ia-step-icon" style={{ color: step.color }}>{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== TECHNOLOGIES SECTION ========== */}
      <section className="ia-tech">
        <div className="ia-container-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="ia-section-header"
          >
            <div className="ia-badge">Stack technique</div>
            <h2>Technologies <span className="ia-gradient-text">de pointe</span></h2>
            <p>Les meilleurs outils d'IA et d'automatisation</p>
          </motion.div>

          <div className="ia-tech-grid">
            {technologies.map((tech, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ y: -5 }}
                className="ia-tech-card"
              >
                <div className="ia-tech-icon" style={{ color: tech.color }}>{tech.icon}</div>
                <h4>{tech.name}</h4>
                <div className="ia-tech-bar">
                  <div className="ia-tech-progress" style={{ backgroundColor: tech.color }} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="ia-final-cta">
        <div className="ia-cta-pattern"></div>
        <div className="ia-cta-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="ia-cta-icon"
          >
            <GiRocketThruster />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Prêt à <span className="ia-gradient-text">automatiser</span> votre entreprise ?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Audit gratuit de vos processus — Découvrez comment l'IA peut transformer votre activité.
          </motion.p>
          <Link to="/reservation-appel">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="ia-cta-button"
            >
              <MdAutoAwesome /> Demander un audit gratuit <FaArrowRight />
            </motion.button>
          </Link>
          <div className="ia-cta-features">
            <div><FaCheckCircle /> Réponse sous 24h</div>
            <div><FaShieldAlt /> Garantie satisfait</div>
            <div><FaUsers /> Support dédié</div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IAEtAutomatisation;